/**
 * Claim presenza cross-app (qcenter, qlive, qprojects, qstreamingweb)
 * e dedup notifiche device online (1 notifica per utente/evento).
 */

import { NOTIFICATION_DEDUP_WINDOW_MS } from './deviceOnlineTiming';

const TAB_ID_STORAGE_KEY = 'qcenter-presence-tab-id';
const PRESENCE_STORAGE_KEY = 'qcenter-session-presence';
const PRESENCE_COOKIE_NAME = 'qcenter_presence';

const NOTIFICATION_DEDUP_COOKIE = 'qcenter_device_online_dedup';
export { NOTIFICATION_DEDUP_WINDOW_MS };
const DEDUP_CHANNEL_NAME = 'qcenter-device-online-notification';

let dedupChannel = null;
const recentNotifications = {};

export function cookieDomainAttr() {
    const host = window.location.hostname;
    if (!host || host === 'localhost' || host === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
        return '';
    }

    const parts = host.split('.');
    if (parts.length < 2) {
        return '';
    }

    return `; Domain=.${parts.slice(-2).join('.')}`;
}

function readCookie(name) {
    const match = document.cookie.match(
        new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`)
    );

    if (!match) {
        return null;
    }

    try {
        return JSON.parse(decodeURIComponent(match[1]));
    } catch (error) {
        return null;
    }
}

function writeCookie(name, payload, maxAgeSeconds) {
    try {
        document.cookie = `${name}=${encodeURIComponent(JSON.stringify(payload))}; Path=/${cookieDomainAttr()}; SameSite=Lax; Max-Age=${maxAgeSeconds}`;
    } catch (error) {
        // ignore
    }
}

export function getPresenceTabId() {
    try {
        let tabId = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
        if (!tabId) {
            tabId = `t${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
            sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId);
        }
        return tabId;
    } catch (error) {
        return `t-fallback-${Date.now()}`;
    }
}

export function readPresenceClaim() {
    let fromLocalStorage = null;
    const fromCookie = readCookie(PRESENCE_COOKIE_NAME);

    try {
        fromLocalStorage = JSON.parse(localStorage.getItem(PRESENCE_STORAGE_KEY) || 'null');
    } catch (error) {
        fromLocalStorage = null;
    }

    if (fromLocalStorage && fromCookie) {
        return (fromCookie.ts || 0) >= (fromLocalStorage.ts || 0) ? fromCookie : fromLocalStorage;
    }

    return fromCookie || fromLocalStorage;
}

export function isPresenceLeaderTab() {
    const claim = readPresenceClaim();
    if (!claim || claim.type === 'release' || !claim.tabId) {
        return false;
    }

    return claim.tabId === getPresenceTabId();
}

export function buildNotificationDedupKey(event) {
    if (event?.event_id) {
        return event.event_id;
    }

    if (event?.serial && event?.detected_at) {
        return `serial-${event.serial}-${event.detected_at}`;
    }

    if (event?.serial) {
        return `serial-${event.serial}`;
    }

    if (event?.seqMission != null) {
        return `mission-${event.seqMission}`;
    }

    return 'serial-unknown';
}

function getDedupChannel() {
    if (dedupChannel) {
        return dedupChannel;
    }

    if (typeof BroadcastChannel === 'undefined') {
        return null;
    }

    try {
        dedupChannel = new BroadcastChannel(DEDUP_CHANNEL_NAME);
        dedupChannel.onmessage = (event) => {
            const key = event.data?.key;
            const ts = event.data?.ts;
            if (key && ts) {
                recentNotifications[key] = ts;
            }
        };
    } catch (error) {
        dedupChannel = null;
    }

    return dedupChannel;
}

function pruneNotificationDedupMap(map, now) {
    Object.keys(map).forEach((key) => {
        if (!map[key] || now - map[key] >= NOTIFICATION_DEDUP_WINDOW_MS) {
            delete map[key];
        }
    });
}

function readNotificationDedupCookie() {
    const raw = readCookie(NOTIFICATION_DEDUP_COOKIE);
    return raw && typeof raw === 'object' ? raw : {};
}

function wasRecentlyNotifiedInMemory(key, now = Date.now()) {
    const ts = recentNotifications[key];
    return ts != null && now - ts < NOTIFICATION_DEDUP_WINDOW_MS;
}

function markNotificationSentInMemory(key, ts = Date.now()) {
    recentNotifications[key] = ts;

    const channel = getDedupChannel();
    if (channel) {
        try {
            channel.postMessage({ key, ts });
        } catch (error) {
            // ignore
        }
    }
}

/**
 * Dedup cross-tab sullo stesso PC (cookie Domain=.hightek.it).
 * Coda backend condivisa per id_utente: tutti i PC connessi ricevono gli stessi eventi.
 */
export function tryAcquireNotificationSlot(dedupKey) {
    getDedupChannel();

    const now = Date.now();
    if (wasRecentlyNotifiedInMemory(dedupKey, now)) {
        return false;
    }

    const cookieMap = readNotificationDedupCookie();
    pruneNotificationDedupMap(cookieMap, now);

    if (cookieMap[dedupKey] && now - cookieMap[dedupKey] < NOTIFICATION_DEDUP_WINDOW_MS) {
        markNotificationSentInMemory(dedupKey, cookieMap[dedupKey]);
        return false;
    }

    cookieMap[dedupKey] = now;
    writeCookie(NOTIFICATION_DEDUP_COOKIE, cookieMap, Math.ceil(NOTIFICATION_DEDUP_WINDOW_MS / 1000) + 5);
    markNotificationSentInMemory(dedupKey, now);

    return true;
}

/** Rilascia lo slot se la Notification non è stata mostrata (permission/bridge fail). */
export function releaseNotificationSlot(dedupKey) {
    delete recentNotifications[dedupKey];

    const cookieMap = readNotificationDedupCookie();
    if (cookieMap[dedupKey] != null) {
        delete cookieMap[dedupKey];
        writeCookie(NOTIFICATION_DEDUP_COOKIE, cookieMap, Math.ceil(NOTIFICATION_DEDUP_WINDOW_MS / 1000) + 5);
    }
}
